/**
 * Silently refreshes the access token using the stored refresh token.
 *
 * Returns the new access token string on success, or null if the refresh
 * token is also expired/missing (meaning the full session is gone and
 * the user needs to log in again).
 *
 * Throws a RefreshServerError for transient failures (5xx, network errors)
 * so callers can distinguish "session expired → logout" (null return) from
 * "server temporarily unavailable → keep session alive" (thrown error).
 */

import { getCustomAuthTokens, storeCustomAuthTokens } from "../features/auth/LoginBox";
import { API_ROUTES } from "../routes";

/** Singleton in-flight promise to prevent concurrent refresh calls. */
let refreshInFlight: Promise<string | null> | null = null;

export const refreshAccessToken = (): Promise<string | null> => {
  // Deduplicate: if a refresh is already in progress, share that promise
  if (refreshInFlight) {
    console.log("[AUTH][datasurfr][refreshAccessToken.ts] Refresh already in flight — reusing existing promise");
    return refreshInFlight;
  }

  refreshInFlight = _doRefresh().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
};

async function _doRefresh(): Promise<string | null> {
  const tokens = getCustomAuthTokens();
  const refreshToken = tokens?.refresh_token;

  if (!refreshToken) {
    console.warn("[AUTH][datasurfr][refreshAccessToken.ts] No refresh token in storage — cannot refresh, session fully expired");
    return null;
  }

  const baseUrl = import.meta.env.VITE_API_INTERNAL_URL;
  console.log("[AUTH][datasurfr][refreshAccessToken.ts] Attempting token refresh | endpoint:", API_ROUTES.GET_NEW_TOKEN);

  try {
    const response = await fetch(`${baseUrl}${API_ROUTES.GET_NEW_TOKEN}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ refreshToken }),
    });

    if (response.status === 401 || response.status === 403) {
      // Refresh token itself is expired or revoked — full session gone
      console.warn("[AUTH][datasurfr][refreshAccessToken.ts] Refresh token rejected (status:", response.status, ") — session fully expired, need login");
      return null;
    }

    if (!response.ok) {
      // 5xx or other server error — transient, not a session expiry. Throw so
      // callers can distinguish this from a null return (which means session expired).
      console.warn("[AUTH][datasurfr][refreshAccessToken.ts] Refresh endpoint returned", response.status, "— transient server error, not treating as session expiry");
      throw new Error(`RefreshServerError:${response.status}`);
    }

    const data = await response.json();
    const newAccessToken: string = data.accessToken;

    if (!newAccessToken) {
      console.warn("[AUTH][datasurfr][refreshAccessToken.ts] Refresh response missing accessToken field");
      return null;
    }

    // Persist the new access token back into sessionStorage
    const existing = getCustomAuthTokens();
    if (existing) {
      storeCustomAuthTokens({
        accessToken: newAccessToken,
        refreshToken: existing.refresh_token,
        idToken: existing.id_token,
        expiresIn: existing.expires_in,
      });
    }

    console.log("[AUTH][datasurfr][refreshAccessToken.ts] Token refresh succeeded — new access token stored");
    return newAccessToken;
  } catch (err) {
    if (err instanceof Error && err.message.startsWith("RefreshServerError")) {
      // Re-throw transient server errors so callers can handle them without logging out
      throw err;
    }
    // Network error — transient, not a session expiry. Throw so callers don't logout.
    console.warn("[AUTH][datasurfr][refreshAccessToken.ts] Network error during refresh — transient, not treating as session expiry:", err);
    throw new Error("RefreshNetworkError");
  }
}
